import { ArrowUp } from 'lucide-react';

import { HelmRelease } from '@/react/kubernetes/helm/types';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Modal } from '@@/modals';
import { Button, LoadingButton } from '@@/buttons';
import { WidgetIcon } from '@@/Widget/WidgetIcon';

import { K8sRegistryAccessNotice } from '../../components/K8sRegistryAccessNotice';

interface Props {
  release: HelmRelease;
  onDismiss: () => void;
}

/**
 * Mirrors UpgradeHelmInnerForm's chrome so the modal doesn't resize once the
 * real form mounts. Shown while UpgradeHelmModal is still resolving the data
 * it needs to compute correct initial form values.
 */
export function UpgradeHelmInnerFormSkeleton({ release, onDismiss }: Props) {
  const environmentId = useEnvironmentId();

  return (
    <Modal
      onDismiss={onDismiss}
      size="xl"
      className="flex h-[80vh] flex-col px-0"
      aria-label="upgrade-helm"
    >
      <Modal.Header
        title={
          <div className="inline-flex items-center gap-1 px-5">
            <WidgetIcon icon={ArrowUp} />
            <h2 className="m-0 ml-1 text-base">Edit/Upgrade {release.name}</h2>
          </div>
        }
        className="border-0 border-b border-solid border-gray-5 th-highcontrast:border-white th-dark:border-gray-7"
      >
        <div className="m-0 flex flex-col gap-2 px-5 pt-2">
          <span>Namespace &apos;{release.namespace}&apos;</span>
          <K8sRegistryAccessNotice
            namespace={release.namespace}
            environmentId={environmentId}
          />
        </div>
      </Modal.Header>
      <div className="flex-1 overflow-y-auto px-5">
        <Modal.Body>
          <div className="form-horizontal animate-pulse">
            {/* mirrors UseGitFormSection's "Deploy from" BoxSelector (Git repository / Helm repository) */}
            <div className="mb-5 h-4 w-24 rounded bg-gray-3 th-dark:bg-gray-8" />
            <div className="mb-8 flex flex-wrap gap-2.5">
              <div className="flex h-36 w-64 flex-col gap-2 rounded border border-solid border-gray-5 p-4 th-dark:border-gray-7">
                <div className="h-8 w-8 rounded-full bg-gray-3 th-dark:bg-gray-8" />
                <div className="h-4 w-2/3 rounded bg-gray-3 th-dark:bg-gray-8" />
                <div className="h-3 w-full rounded bg-gray-3 th-dark:bg-gray-8" />
                <div className="h-3 w-5/6 rounded bg-gray-3 th-dark:bg-gray-8" />
              </div>
              <div className="flex h-36 w-64 flex-col gap-2 rounded border border-solid border-gray-5 p-4 th-dark:border-gray-7">
                <div className="h-8 w-8 rounded-full bg-gray-3 th-dark:bg-gray-8" />
                <div className="h-4 w-2/3 rounded bg-gray-3 th-dark:bg-gray-8" />
                <div className="h-3 w-full rounded bg-gray-3 th-dark:bg-gray-8" />
                <div className="h-3 w-5/6 rounded bg-gray-3 th-dark:bg-gray-8" />
              </div>
            </div>
            {/* mirrors the "Helm chart" section (registry + chart selects) */}
            <div className="mb-5 h-6 w-40 rounded bg-gray-3 th-dark:bg-gray-8" />
            <div className="mb-8 flex flex-col gap-4">
              <div className="h-10 w-full rounded bg-gray-3 th-dark:bg-gray-8" />
              <div className="h-10 w-full rounded bg-gray-3 th-dark:bg-gray-8" />
            </div>
            {/* mirrors the "Upgrade options" section (version select, rollback checkbox, values editor) */}
            <div className="mb-5 h-6 w-40 rounded bg-gray-3 th-dark:bg-gray-8" />
            <div className="flex flex-col gap-4">
              <div className="h-10 w-full rounded bg-gray-3 th-dark:bg-gray-8" />
              <div className="h-10 w-2/3 rounded bg-gray-3 th-dark:bg-gray-8" />
              <div className="h-24 w-full rounded bg-gray-3 th-dark:bg-gray-8" />
            </div>
          </div>
        </Modal.Body>
      </div>
      <div className="border-0 border-t border-solid border-gray-5 px-5 th-highcontrast:border-white th-dark:border-gray-7">
        <Modal.Footer>
          <div className="flex flex-1 justify-between">
            <Button
              onClick={onDismiss}
              color="secondary"
              key="cancel-button"
              size="medium"
              data-cy="cancel-button-cy"
            >
              Cancel
            </Button>
            <LoadingButton
              type="button"
              color="primary"
              key="update-button"
              size="medium"
              disabled
              isLoading
              data-cy="update-button-cy"
              loadingText="Upgrade"
            >
              Upgrade
            </LoadingButton>
          </div>
        </Modal.Footer>
      </div>
    </Modal>
  );
}
